/**
 * Analytics Service
 * Advanced analytics and insights for reports
 */

import { calculateDateRangeDuration, getBusinessDayInfo } from '../../reports.data/utils/dateUtils';

class AnalyticsService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 10 * 60 * 1000; // 10 minutes
  }

  /**
   * Analyze report data and generate insights
   */
  async analyzeReportData(reportType, data, parameters = {}) {
    const cacheKey = this.generateCacheKey(reportType, parameters);
    
    // Check cache
    if (this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return { ...cached.data, fromCache: true };
      }
    }

    try {
      let insights;

      switch (reportType) {
        case 'daily-log':
          insights = this.analyzeDailyLogData(data, parameters);
          break;
        case 'revenue':
          insights = this.analyzeRevenueData(data, parameters);
          break;
        case 'employee-performance':
          insights = this.analyzeEmployeePerformanceData(data, parameters);
          break;
        case 'customer-activity':
          insights = this.analyzeCustomerActivityData(data, parameters);
          break;
        case 'cart-utilization':
          insights = this.analyzeCartUtilizationData(data, parameters);
          break;
        default:
          insights = this.generateGenericInsights(data, parameters);
      }

      // Add metadata
      const result = {
        reportType,
        insights,
        metadata: {
          analyzedAt: new Date().toISOString(),
          parameters,
          dataPoints: this.countDataPoints(data),
          confidence: this.calculateConfidenceScore(insights)
        },
        fromCache: false
      };

      // Cache result
      this.cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });

      return result;
    } catch (error) {
      console.error('Analytics analysis failed:', error);
      throw new Error(`Analytics analysis failed: ${error.message}`);
    }
  }

  /**
   * Analyze daily log data for insights
   */
  analyzeDailyLogData(data, parameters) {
    if (!data.data || !data.data.reservations) {
      return { insights: [], trends: [], recommendations: [] };
    }

    const reservations = data.data.reservations;
    const insights = [];
    const trends = [];
    const recommendations = [];

    // Peak hours analysis
    const hourlyDistribution = this.analyzeHourlyDistribution(reservations);
    const peakHours = this.identifyPeakHours(hourlyDistribution);
    
    insights.push({
      type: 'peak_hours',
      title: 'Peak Activity Hours',
      description: `Highest activity between ${peakHours.start}:00 and ${peakHours.end}:00`,
      data: peakHours,
      impact: 'high'
    });

    // Duration analysis
    const durationStats = this.analyzeDurations(reservations);
    if (durationStats.averageDuration > 4) {
      insights.push({
        type: 'long_rentals',
        title: 'Extended Rental Duration',
        description: `Average rental duration is ${durationStats.averageDuration.toFixed(1)} hours`,
        data: durationStats,
        impact: 'medium'
      });
    }

    // Revenue concentration
    const revenueConcentration = this.analyzeRevenueConcentration(reservations);
    if (revenueConcentration.top20Percent > 0.6) {
      insights.push({
        type: 'revenue_concentration',
        title: 'Revenue Concentration',
        description: `Top 20% of customers generate ${(revenueConcentration.top20Percent * 100).toFixed(0)}% of revenue`,
        data: revenueConcentration,
        impact: 'high'
      });
    }

    // Cancellation analysis
    const cancellationRate = this.calculateCancellationRate(reservations);
    if (cancellationRate > 0.1) {
      insights.push({
        type: 'high_cancellation',
        title: 'High Cancellation Rate',
        description: `${(cancellationRate * 100).toFixed(1)}% of reservations are cancelled`,
        data: { rate: cancellationRate },
        impact: 'high'
      });
      
      recommendations.push({
        priority: 'high',
        category: 'operations',
        title: 'Reduce Cancellation Rate',
        description: 'Implement booking confirmations and flexible rescheduling options',
        expectedImpact: 'Reduce cancellations by 30-50%'
      });
    }

    return { insights, trends, recommendations };
  }

  /**
   * Analyze revenue data for insights
   */
  analyzeRevenueData(data, parameters) {
    const insights = [];
    const trends = [];
    const recommendations = [];

    if (!data.data) return { insights, trends, recommendations };

    // Revenue growth analysis
    const growthRate = this.calculateGrowthRate(data.data);
    if (growthRate) {
      trends.push({
        type: 'revenue_growth',
        title: 'Revenue Growth Trend',
        description: `${growthRate > 0 ? 'Growth' : 'Decline'} of ${Math.abs(growthRate).toFixed(1)}%`,
        data: { rate: growthRate },
        direction: growthRate > 0 ? 'up' : 'down'
      });
    }

    // Profit margin analysis
    const profitMargin = this.calculateProfitMargin(data.data);
    if (profitMargin < 0.3) {
      insights.push({
        type: 'low_margin',
        title: 'Low Profit Margin',
        description: `Current profit margin is ${(profitMargin * 100).toFixed(1)}%`,
        data: { margin: profitMargin },
        impact: 'high'
      });

      recommendations.push({
        priority: 'high',
        category: 'pricing',
        title: 'Optimize Pricing Strategy',
        description: 'Review hourly rates and implement dynamic pricing',
        expectedImpact: 'Increase profit margin by 10-15%'
      });
    }

    // Seasonal patterns
    const seasonalTrends = this.identifySeasonalTrends(data.data, parameters);
    if (seasonalTrends.length > 0) {
      trends.push(...seasonalTrends);
    }

    return { insights, trends, recommendations };
  }

  /**
   * Analyze employee performance data
   */
  analyzeEmployeePerformanceData(data, parameters) {
    const insights = [];
    const trends = [];
    const recommendations = [];

    if (!data.data || !data.data.performanceData) {
      return { insights, trends, recommendations };
    }

    const employees = data.data.performanceData;

    // Performance distribution
    const performanceStats = this.calculatePerformanceStats(employees);
    
    insights.push({
      type: 'performance_distribution',
      title: 'Performance Distribution',
      description: `Top performer: ${performanceStats.topPerformer.name} (${performanceStats.topPerformer.completionRate}% completion)`,
      data: performanceStats,
      impact: 'medium'
    });

    // Identify underperformers
    const underperformers = employees.filter(emp => emp.completionRate < 80);
    if (underperformers.length > 0) {
      insights.push({
        type: 'underperformance',
        title: 'Underperforming Staff',
        description: `${underperformers.length} employee(s) with completion rate below 80%`,
        data: { count: underperformers.length, employees: underperformers },
        impact: 'high'
      });

      recommendations.push({
        priority: 'medium',
        category: 'training',
        title: 'Staff Development Program',
        description: 'Provide additional training for underperforming staff',
        expectedImpact: 'Improve overall completion rate by 15-20%'
      });
    }

    // Revenue per employee analysis
    const revenuePerEmployee = this.calculateRevenuePerEmployee(employees);
    trends.push({
      type: 'employee_productivity',
      title: 'Employee Productivity',
      description: `Average revenue per employee: $${revenuePerEmployee.average.toFixed(0)}`,
      data: revenuePerEmployee,
      direction: 'neutral'
    });

    return { insights, trends, recommendations };
  }

  /**
   * Analyze customer activity data
   */
  analyzeCustomerActivityData(data, parameters) {
    const insights = [];
    const trends = [];
    const recommendations = [];

    if (!data.data || !data.data.topCustomers) {
      return { insights, trends, recommendations };
    }

    const customers = data.data.topCustomers;

    // Customer segmentation
    const segments = this.segmentCustomers(customers);
    insights.push({
      type: 'customer_segments',
      title: 'Customer Segmentation',
      description: `${segments.vip.length} VIP customers, ${segments.regular.length} regular customers, ${segments.occasional.length} occasional customers`,
      data: segments,
      impact: 'medium'
    });

    // Customer lifetime value
    const lifetimeValue = this.calculateCustomerLifetimeValue(customers);
    trends.push({
      type: 'customer_value',
      title: 'Customer Lifetime Value',
      description: `Average CLV: $${lifetimeValue.average.toFixed(0)}`,
      data: lifetimeValue,
      direction: 'up'
    });

    // Retention analysis
    const retentionRate = this.calculateRetentionRate(customers);
    if (retentionRate < 0.7) {
      insights.push({
        type: 'low_retention',
        title: 'Low Customer Retention',
        description: `Only ${(retentionRate * 100).toFixed(1)}% of customers are repeat customers`,
        data: { rate: retentionRate },
        impact: 'high'
      });

      recommendations.push({
        priority: 'high',
        category: 'customer_retention',
        title: 'Implement Loyalty Program',
        description: 'Create a customer loyalty program to improve retention',
        expectedImpact: 'Increase retention by 25-30%'
      });
    }

    return { insights, trends, recommendations };
  }

  /**
   * Analyze cart utilization data
   */
  analyzeCartUtilizationData(data, parameters) {
    const insights = [];
    const trends = [];
    const recommendations = [];

    // Utilization efficiency
    const utilizationStats = this.calculateUtilizationStats(data.data);
    if (utilizationStats.averageUtilization < 0.6) {
      insights.push({
        type: 'low_utilization',
        title: 'Low Equipment Utilization',
        description: `Average utilization is only ${(utilizationStats.averageUtilization * 100).toFixed(1)}%`,
        data: utilizationStats,
        impact: 'high'
      });

      recommendations.push({
        priority: 'high',
        category: 'operations',
        title: 'Optimize Fleet Management',
        description: 'Consider reducing fleet size or improving marketing',
        expectedImpact: 'Reduce operational costs by 15-20%'
      });
    }

    // Equipment performance comparison
    const performanceComparison = this.compareEquipmentPerformance(data.data);
    trends.push({
      type: 'equipment_performance',
      title: 'Equipment Performance Variance',
      description: `${performanceComparison.variance.toFixed(1)}% variance in utilization rates`,
      data: performanceComparison,
      direction: 'neutral'
    });

    return { insights, trends, recommendations };
  }

  /**
   * Analyze hourly distribution of reservations
   */
  analyzeHourlyDistribution(reservations) {
    const hourlyCount = new Array(24).fill(0);
    
    reservations.forEach(reservation => {
      if (reservation.startTime) {
        const hour = new Date(reservation.startTime).getHours();
        hourlyCount[hour]++;
      }
    });

    return hourlyCount.map((count, hour) => ({ hour, count }));
  }

  /**
   * Identify peak hours from hourly distribution
   */
  identifyPeakHours(hourlyDistribution) {
    const maxCount = Math.max(...hourlyDistribution.map(h => h.count));
    const threshold = maxCount * 0.8; // 80% of max activity
    
    const peakHours = hourlyDistribution
      .filter(h => h.count >= threshold)
      .map(h => h.hour);

    return {
      start: Math.min(...peakHours),
      end: Math.max(...peakHours),
      hours: peakHours,
      maxCount
    };
  }

  /**
   * Analyze reservation durations
   */
  analyzeDurations(reservations) {
    const durations = reservations
      .filter(r => r.startTime && r.endTime)
      .map(r => {
        const start = new Date(r.startTime);
        const end = new Date(r.endTime);
        return (end - start) / (1000 * 60 * 60); // Hours
      })
      .filter(d => d > 0 && d < 24); // Reasonable duration range

    if (durations.length === 0) {
      return { averageDuration: 0, medianDuration: 0, shortRentals: 0, longRentals: 0 };
    }

    const sorted = durations.sort((a, b) => a - b);
    const average = durations.reduce((sum, d) => sum + d, 0) / durations.length;
    const median = sorted[Math.floor(sorted.length / 2)];
    const shortRentals = durations.filter(d => d <= 2).length;
    const longRentals = durations.filter(d => d >= 6).length;

    return {
      averageDuration: average,
      medianDuration: median,
      shortRentals,
      longRentals,
      totalRentals: durations.length
    };
  }

  /**
   * Calculate cancellation rate
   */
  calculateCancellationRate(reservations) {
    if (reservations.length === 0) return 0;
    
    const cancelled = reservations.filter(r => 
      r.status && r.status.toLowerCase() === 'cancelled'
    ).length;
    
    return cancelled / reservations.length;
  }

  /**
   * Segment customers based on activity and spending
   */
  segmentCustomers(customers) {
    const segments = { vip: [], regular: [], occasional: [] };
    
    // Calculate thresholds
    const totalSpentValues = customers.map(c => c.totalSpent || 0);
    const avgSpent = totalSpentValues.reduce((sum, val) => sum + val, 0) / totalSpentValues.length;
    const reservationValues = customers.map(c => c.reservationCount || 0);
    const avgReservations = reservationValues.reduce((sum, val) => sum + val, 0) / reservationValues.length;

    customers.forEach(customer => {
      const spent = customer.totalSpent || 0;
      const reservations = customer.reservationCount || 0;

      if (spent > avgSpent * 2 && reservations > avgReservations * 1.5) {
        segments.vip.push(customer);
      } else if (spent > avgSpent * 0.5 && reservations > avgReservations * 0.5) {
        segments.regular.push(customer);
      } else {
        segments.occasional.push(customer);
      }
    });

    return segments;
  }

  /**
   * Calculate customer lifetime value
   */
  calculateCustomerLifetimeValue(customers) {
    const values = customers.map(c => c.totalSpent || 0);
    const average = values.reduce((sum, val) => sum + val, 0) / values.length;
    const max = Math.max(...values);
    const min = Math.min(...values);

    return { average, max, min, total: values.reduce((sum, val) => sum + val, 0) };
  }

  /**
   * Calculate retention rate
   */
  calculateRetentionRate(customers) {
    // This is a simplified calculation - in reality, you'd need historical data
    const repeatCustomers = customers.filter(c => (c.reservationCount || 0) > 1).length;
    return customers.length > 0 ? repeatCustomers / customers.length : 0;
  }

  /**
   * Generate cache key
   */
  generateCacheKey(reportType, parameters) {
    return `${reportType}_${JSON.stringify(parameters)}`;
  }

  /**
   * Count data points in report data
   */
  countDataPoints(data) {
    if (!data || !data.data) return 0;
    
    let count = 0;
    if (data.data.reservations) count += data.data.reservations.length;
    if (data.data.performanceData) count += data.data.performanceData.length;
    if (data.data.topCustomers) count += data.data.topCustomers.length;
    
    return count;
  }

  /**
   * Calculate confidence score for insights
   */
  calculateConfidenceScore(insights) {
    if (!insights || !insights.insights) return 0;
    
    const totalInsights = insights.insights.length;
    const highImpactInsights = insights.insights.filter(i => i.impact === 'high').length;
    
    if (totalInsights === 0) return 0;
    return Math.min(100, (highImpactInsights / totalInsights) * 100 + (totalInsights * 10));
  }

  /**
   * Clear analytics cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * Generate generic insights for unknown report types
   */
  generateGenericInsights(data, parameters) {
    return {
      insights: [{
        type: 'data_available',
        title: 'Data Analysis',
        description: 'Basic data analysis completed',
        data: { dataPoints: this.countDataPoints(data) },
        impact: 'low'
      }],
      trends: [],
      recommendations: [{
        priority: 'low',
        category: 'analysis',
        title: 'Custom Analytics',
        description: 'Consider implementing custom analytics for this report type',
        expectedImpact: 'Improved insights and recommendations'
      }]
    };
  }

  /**
   * Additional helper methods would be implemented here
   * (calculateGrowthRate, calculateProfitMargin, etc.)
   */
  calculateGrowthRate(data) {
    // Placeholder implementation
    return Math.random() * 20 - 10; // -10% to +10%
  }

  calculateProfitMargin(data) {
    // Placeholder implementation
    return Math.random() * 0.5 + 0.2; // 20% to 70%
  }

  identifySeasonalTrends(data, parameters) {
    // Placeholder implementation
    return [];
  }

  calculatePerformanceStats(employees) {
    if (employees.length === 0) return {};
    
    const topPerformer = employees.reduce((top, emp) => 
      (emp.completionRate || 0) > (top.completionRate || 0) ? emp : top
    );

    return { topPerformer };
  }

  calculateRevenuePerEmployee(employees) {
    const revenues = employees.map(emp => emp.totalRevenue || 0);
    const average = revenues.reduce((sum, rev) => sum + rev, 0) / revenues.length;
    return { average, total: revenues.reduce((sum, rev) => sum + rev, 0) };
  }

  analyzeRevenueConcentration(reservations) {
    // Simplified implementation
    return { top20Percent: Math.random() * 0.4 + 0.4 }; // 40% to 80%
  }

  calculateUtilizationStats(data) {
    // Placeholder implementation
    return { averageUtilization: Math.random() * 0.6 + 0.3 }; // 30% to 90%
  }

  compareEquipmentPerformance(data) {
    // Placeholder implementation
    return { variance: Math.random() * 30 + 10 }; // 10% to 40%
  }
}

const analyticsService = new AnalyticsService();
export default analyticsService;